import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Avatar, Box, CircularProgress, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import logOut from "../../assets/log-out.svg";
import { useAuth } from "../../context/AuthContext";
import { useLanguages } from "../../context/LanguagesContext";
import { gray } from "../../theme/themePrimitives";
import { getInitials, truncateName } from "../../utils/helpers";
import { handleLogout } from "../../utils/authUtils";
import { MenuItem } from "./MenuItem";
import { MainMenuItem } from "./sidebarData";
import { useCustomisation } from "../../context/CustomisationContext";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { useGuestBootstrap } from "../../hooks/useGuestBootstrap";

interface UserMenuProps {
  sidebarOpen: boolean;
  sidebarShade: string;
  sidebarTextColor: string;
  mobileView?: boolean;
  setSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onLanguageModalOpen: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  sidebarOpen,
  sidebarShade,
  sidebarTextColor,
  mobileView,
  setSidebarOpen,
  onLanguageModalOpen,
}) => {
  const [username, setUsername] = useState("");
  const navigate = useNavigate();
  const { auth, dispatch: authDispatch } = useAuth();
  const { dispatch: portalsDispatch } = usePortal();
  const { dispatch: customisationsDispatch } = useCustomisation();
  const { languages, getLanguageDetails } = useLanguages();
  const runGuestBootstrap = useGuestBootstrap();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.user) {
      setUsername(`${auth.user.firstName} ${auth.user.lastName}`);
    }
  }, [auth.user]);

  const selectedLanguage = useMemo(() => {
    const languageDetails = getLanguageDetails(languages.selectedLanguage);
    return languageDetails || { name: "English", flag: "" };
  }, [languages.selectedLanguage, getLanguageDetails]);

  const languageFlagIcon = useMemo(
    () =>
      selectedLanguage.flag ? (
        <Box
          component="img"
          src={selectedLanguage.flag}
          alt={selectedLanguage.name}
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            objectFit: "cover",
            border: `1px solid ${gray[300]}`,
          }}
        />
      ) : null,
    [selectedLanguage.flag, selectedLanguage.name],
  );

  const handleLogoutClick = useCallback(async () => {
    if (auth.user?.email) {
      await handleLogout({
        userEmail: auth.user.email,
        authDispatch,
        customisationsDispatch,
        portalsDispatch,
        runGuestBootstrap,
        navigate,
        showToast,
        setLoading,
      });
    }
  }, [
    auth.user?.email,
    authDispatch,
    customisationsDispatch,
    portalsDispatch,
    navigate,
    runGuestBootstrap,
    setLoading,
    showToast,
  ]);

  const userInitialsIcon = useMemo(
    () => (
      <Avatar
        sx={{
          width: 24,
          height: 24,
          bgcolor: gray[200],
          color: gray[600],
          fontSize: "0.75rem",
          fontWeight: 600,
        }}
      >
        {getInitials(auth.user?.firstName ?? "", auth.user?.lastName ?? "")}
      </Avatar>
    ),
    [auth.user?.firstName, auth.user?.lastName],
  );

  const menuItems: MainMenuItem[] = useMemo(
    () => [
      {
        label: truncateName(username),
        icon: userInitialsIcon,
        route: "/profile",
        tooltip: username,
      },
      {
        label: selectedLanguage.name,
        icon: languageFlagIcon,
        tooltip: selectedLanguage.name,
        onClick: onLanguageModalOpen,
      },
      {
        label: "Log out",
        icon: logOut,
        tooltip: "Log out",
        onClick: handleLogoutClick,
      },
    ],
    [
      username,
      userInitialsIcon,
      selectedLanguage,
      languageFlagIcon,
      onLanguageModalOpen,
      handleLogoutClick,
    ],
  );

  if (!mobileView) {
    return null;
  }

  return (
    <>
      <Divider sx={{ marginY: "1rem", backgroundColor: sidebarTextColor }} />
      {menuItems.map((item, index) =>
        loading && item.label === "Log out" ? (
          <CircularProgress />
        ) : (
          <MenuItem
            key={index}
            item={item}
            sidebarOpen={sidebarOpen}
            sidebarShade={sidebarShade}
            sidebarTextColor={sidebarTextColor}
            mobileView={mobileView}
            setSidebarOpen={setSidebarOpen}
          />
        ),
      )}
    </>
  );
};
